import { DialogComponent } from './dialog.js';

/**
 * A custom element that manages a modal dialog with URL hash support.
 * Opens when the URL hash matches the modal ID, closes when hash is removed.
 *
 * @extends DialogComponent
 */
export class ModalComponent extends DialogComponent {
  #modalId = '';

  connectedCallback() {
    super.connectedCallback();

    // Get modal ID from the dialog element's id attribute
    this.#modalId = this.refs.dialog.id;
    if (!this.#modalId) {
      console.warn('ModalComponent: dialog element must have an id attribute');
      return;
    }

    // If in preview mode, open the modal immediately with full modal behavior
    if (this.hasAttribute('data-preview') && !this.refs?.dialog?.open) {
      this.showDialog();
    }

    this.#checkHash();
    window.addEventListener('hashchange', this.#handleHashChange);
    window.addEventListener('bodi:openModal', this.#handleOpenModalEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.#handleHashChange);
    window.removeEventListener('bodi:openModal', this.#handleOpenModalEvent);
  }

  /**
   * Shows the dialog and updates URL hash.
   */
  showDialog() {
    super.showDialog();
    
    // Update URL hash if it doesn't already match this modal
    if (window.location.hash !== `#${this.#modalId}`) {
      const url = new URL(window.location.href);
      url.hash = this.#modalId;
      history.replaceState(null, '', url);
    }
  }

  /**
   * Closes the dialog and removes hash from URL if it matches.
   */
  async closeDialog() {
    await super.closeDialog();
    
    // Remove hash from URL after closing if it matches this modal
    if (window.location.hash === `#${this.#modalId}`) {
      const url = new URL(window.location.href);
      url.hash = '';
      history.replaceState(null, '', url);
    }
  }

  #handleHashChange = () => {
    this.#checkHash();
  };

  /**
   * Handle custom modal open event from bodi-button
   * @param {CustomEvent} event - Custom event with modal ID in detail
   */
  #handleOpenModalEvent = (event) => {
    const { modalId } = event.detail;
    
    // Only open if this is the target modal
    if (modalId === this.#modalId && !this.refs?.dialog?.open) {
      this.showDialog();
    }
  };

  #checkHash() {
    if (!this.#modalId) return;

    // In preview mode, keep modal open regardless of hash
    if (this.hasAttribute('data-preview') && !this.refs?.dialog?.open) {
      this.showDialog();
      return;
    }

    const currentHash = window.location.hash.slice(1); // Remove the # symbol

    if (currentHash === this.#modalId) {
      // Hash matches modal ID, open the modal
      if (!this.refs.dialog.open) {
        this.showDialog();
      }
    } else {
      // Hash doesn't match, close the modal if it's open
      if (this.refs.dialog.open) {
        this.closeDialog();
      }
    }
  }
}

if (!customElements.get('modal-component')) {
  customElements.define('modal-component', ModalComponent);
}
